import React, { useCallback, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ForgotPasswordPage } from "../domains/auth/components/ForgotPasswordPage";
import { NewPasswordRequiredPage } from "../domains/auth/components/NewPasswordRequiredPage";
import { SignInPage } from "../domains/auth/components/SignInPage";
import {
  getCurrentUser,
  isAuthEnabled,
  isAuthenticated,
  signOut,
} from "../domains/auth/client";
import { App } from "./App";

interface AuthSnapshot {
  authenticated: boolean;
  accountEmail: string | null;
}

interface NewPasswordState {
  session: string;
  username: string;
}

function buildAuthSnapshot(): AuthSnapshot {
  if (!isAuthEnabled()) {
    return {
      authenticated: true,
      accountEmail: null,
    };
  }

  const authenticated = isAuthenticated();
  const user = authenticated ? getCurrentUser() : null;

  return {
    authenticated,
    accountEmail: user?.email ?? user?.username ?? null,
  };
}

export function AppRouter(): JSX.Element {
  const [, setAuthVersion] = useState(0);
  const [newPasswordState, setNewPasswordState] = useState<NewPasswordState | null>(null);
  const snapshot = buildAuthSnapshot();
  const authEnabled = isAuthEnabled();

  const handleAuthComplete = useCallback(() => {
    setAuthVersion((value) => value + 1);
  }, []);

  const handleNewPasswordRequired = useCallback((session: string, username: string) => {
    setNewPasswordState({ session, username });
  }, []);

  const handleSignOut = useCallback(() => {
    signOut();
    setNewPasswordState(null);
    setAuthVersion((value) => value + 1);
  }, []);

  if (!authEnabled) {
    return (
      <Routes>
        <Route path="*" element={<App />} />
      </Routes>
    );
  }

  // If a new-password challenge is pending, show that page regardless of route
  if (newPasswordState) {
    return (
      <NewPasswordRequiredPage
        session={newPasswordState.session}
        username={newPasswordState.username}
        onComplete={() => {
          setNewPasswordState(null);
          handleAuthComplete();
        }}
      />
    );
  }

  return (
    <Routes>
      <Route
        path="/signin"
        element={
          snapshot.authenticated ? (
            <Navigate to="/" replace />
          ) : (
            <SignInPage
              onAuthComplete={handleAuthComplete}
              onNewPasswordRequired={handleNewPasswordRequired}
            />
          )
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route
        path="/"
        element={
          snapshot.authenticated ? (
            <App accountEmail={snapshot.accountEmail} onSignOut={handleSignOut} />
          ) : (
            <Navigate to="/signin" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to={snapshot.authenticated ? "/" : "/signin"} replace />} />
    </Routes>
  );
}
